import { isFuture } from 'date-fns'

import type {
  TermsMetadataResponseInner,
  TermsValue,
} from '@b-stock/account-api-client'
import { augmentTokenPayload } from '@b-stock/authorization-core'
import type {
  OrchestratedListingDetails,
  OrchestratedAuctionListingDetails,
  OrchestratedCommonListingDetails,
  OrchestratedLotWithGroups,
  LeanLot,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  getAccountsClient,
  getAuctionWithBid,
  getBidsClient,
  BidStatus,
  fetchAllResults,
  getGroupsClient,
  sanitizeAccessToken,
  getLotsClient,
  augmentLots,
  makeOptionallyAuthenticatedQuery,
  augmentOfferingItems,
  fetchOfferingsByListingIds,
  getOffersClient,
  getOrdersClient,
  getStorefrontsClient,
  getSellerInfo,
  fetchContractById,
  getListing,
  getTermsClient,
  LeanLotExclude,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  buildAxiosRequestConfig,
  decodeAccessToken,
} from '@b-stock/fe-core/bstock-react'
import type {
  FirstDibsDto,
  FirstDibsEntryDto,
  ItemDto,
} from '@b-stock/listing-api-client'
import { ItemDtoStatusEnum } from '@b-stock/listing-api-client'
import { OrdersApiControllerFindAllSubjectTypeEnum } from '@b-stock/order-api-client'
import type { DealsEnum } from '@b-stock/search-api-client'

import { hasPermission, PermissionGranule } from '@/helpers/permissions'
import getAllListingsSearchClient from '@/queries/getAllListingsSearchClient'

/**
 * A First Dibs group as the LDP reads it: the listing client's entry plus
 * `windowStart` (eventStartTime − windowOffset hrs). `earlyPrice` is in
 * dollars, the same unit as the auction's buyNow.price.
 *
 * `listing-api-client` types `windowStart` on the *response* DTO only, as an
 * ISO string, so the extension stays for `FirstDibsEntryDto` and matches that
 * type. The auction attributes can hand back a `Date`; it is normalised to
 * ISO where it is read, not carried through the type.
 */
export type FirstDibsGroup = FirstDibsEntryDto & {
  /** ISO-8601; a Date from the auction attributes is converted on the way in */
  windowStart?: string
}

export type FirstDibsWithWindowStart = Omit<FirstDibsDto, 'groups'> & {
  groups?: FirstDibsGroup[]
}

type AuctionWithFirstDibsAttributes = {
  attributes?: {
    firstDibs?: {
      groups?: Array<{ buyerGroupId: string; windowStart?: string | Date }>
    }
  }
}

/**
 * Returns a copy of the listing's firstDibs with any group missing
 * windowStart filled in from auction.attributes.firstDibs (the auction
 * service stores it when it receives the listing-status-updated event).
 * Neither generated client types these fields yet. The listing's groups are
 * already filtered to the buyer's verified groups. Returns undefined when
 * there is nothing to fill in, so callers keep the listing's own value.
 */
export const withFirstDibsWindowStart = (
  firstDibs: FirstDibsWithWindowStart | undefined,
  auction: object
): FirstDibsWithWindowStart | undefined => {
  const auctionGroups = (auction as AuctionWithFirstDibsAttributes).attributes
    ?.firstDibs?.groups
  const groups = firstDibs?.groups
  if (
    !firstDibs ||
    !groups?.some((g) => !g.windowStart) ||
    !auctionGroups?.length
  ) {
    return undefined
  }

  const windowStartByGroup = new Map(
    auctionGroups.map((g) => [g.buyerGroupId, g.windowStart])
  )
  return {
    ...firstDibs,
    groups: groups.map((g) => {
      const windowStart = windowStartByGroup.get(g.buyerGroupId)
      if (g.windowStart || !windowStart) return g
      // listing-api-client types windowStart as an ISO string, so a Date
      // from the auction attributes is stored the same way.
      return {
        ...g,
        windowStart:
          windowStart instanceof Date ? windowStart.toISOString() : windowStart,
      }
    }),
  }
}

/**
 * getAuctionWithBid derives bidStatus by finding the user's bid via
 * winningBidId. However, buyers receive LimitedAuction from the auction API,
 * which omits winningBidId — so bid is always null and bidStatus defaults to
 * NO_BID. LimitedAuction does expose `winning` and `userBidAmount` fields that
 * let us derive the correct status on the initial page load, before any Pusher
 * private events arrive.
 */
const patchBidStatusFromLimitedAuction = (
  auction: OrchestratedAuctionListingDetails['auction']
): OrchestratedAuctionListingDetails['auction'] => {
  if (auction.bidStatus !== BidStatus.NO_BID) return auction

  // `winning` exists on LimitedAuction but not on the full Auction type
  const winning = (auction as { winning?: boolean }).winning
  if (typeof winning !== 'boolean') return auction

  const userBidAmount =
    (auction as { userBidAmount?: number }).userBidAmount ?? 0

  let bidStatus: BidStatus = BidStatus.NO_BID
  const endTimePast = auction.actualEndTime
    ? !isFuture(auction.actualEndTime)
    : false
  if (auction.closed || endTimePast) {
    if (winning) bidStatus = BidStatus.WON
    else if (userBidAmount > 0) bidStatus = BidStatus.LOST
  } else if (winning) {
    bidStatus = BidStatus.WINNING
  } else if (userBidAmount > 0) {
    bidStatus = BidStatus.LOSING
  }

  if (bidStatus === BidStatus.NO_BID) return auction
  // Fallback: synthesize bid.bidAmount from userBidAmount if the bid wasn't
  // already set by the getAccountUniqueBids call in getListingDetails.
  // This handles the case where that call fails or is skipped.
  const synthesizedBid = { bidAmount: userBidAmount, canceled: false }
  const patched: OrchestratedAuctionListingDetails['auction'] = {
    ...auction,
    bidStatus,
    ...(auction.bid === null && userBidAmount > 0
      ? {
          bid: synthesizedBid as NonNullable<
            OrchestratedAuctionListingDetails['auction']['bid']
          >,
        }
      : {}),
  }
  return patched
}

/**
 * Strongly type actual results to ensure listingDetailsQuery and
 * searchListingDetailsQuery stay in sync.
 *
 * The properties in "ListingDetailsExtension" were undocumented. Do not do
 * this. All properties must be accounted for in these types to stay in sync
 * and obtain the benefits of type safety everywhere this data is used.
 */
export type ListingDetailsExtension = {
  acceptedPolicies: { [key: string]: TermsValue } | null
  deal: DealsEnum | null
  problemFreeOrderRate: number | null
  termsMetadata: TermsMetadataResponseInner | null
  typicalSellingPrice: number | null
}

export type ListingDetailsCommon = OrchestratedCommonListingDetails &
  ListingDetailsExtension

export type ListingDetails = OrchestratedListingDetails &
  ListingDetailsExtension

const getListingDetails = async (
  accessToken: string | null,
  id: string,
  accountId?: string | null,
  incrementViewCount?: boolean
): Promise<ListingDetails> => {
  const accountsClient = getAccountsClient()
  const groupsClient = getGroupsClient()
  const lotsClient = getLotsClient()
  const searchClient = getAllListingsSearchClient()
  const storefrontsClient = getStorefrontsClient()
  const offersClient = getOffersClient()
  const reqOpts = buildAxiosRequestConfig({ accessToken })

  /**
   * Step 1: fetches based on function props
   */
  const listing = await getListing(id, accessToken, incrementViewCount)

  // Fetch order data if listing has been sold to populate soldAs field
  if (listing.buyer?.accountId && accessToken) {
    try {
      const ordersClient = getOrdersClient()
      const { data } = await ordersClient.ordersApiControllerFindAll(
        {
          listingId: id,
          orderType: ['LISTING'],
          subjectType: [OrdersApiControllerFindAllSubjectTypeEnum.Buyer],
        },
        reqOpts
      )

      // Find the order for this specific listing
      const order = data.orders.find((o) => o.listing?.id === id)
      if (order?.listing?.soldAs) {
        listing.soldAs = order.listing.soldAs
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // Order might not exist yet - continue without soldAs
    }
  }

  const {
    accountId: sellerId,
    lotId,
    pricingStrategy,
    storefrontId,
    termsRestrictions,
  } = listing

  if (!lotId) {
    throw new Error('No lot associated with this listing, this is unexpected.')
  }

  /**
   * Step 2: fetches which depends on step 1 responses
   */
  const lotPromise = lotsClient.findOneLot(
    { id: lotId, exclude: LeanLotExclude },
    reqOpts
  )
  const contractPromise = fetchContractById(listing, accessToken)

  const isAuctionListing = pricingStrategy === 'AUCTION'

  const auctionPromise = isAuctionListing
    ? getAuctionWithBid({
        accessToken,
        accountId,
        listingId: id,
      })
    : null

  const offeringsPromise =
    accessToken && !isAuctionListing
      ? fetchOfferingsByListingIds([id], accessToken)
      : null

  const searchDataPromise = isAuctionListing
    ? searchClient
        .allListings({ id: [id], limit: 1 }, reqOpts)
        .then((response) => {
          const listing = response.data.listings[0] ?? null
          return listing
        })
        .catch(() => null)
    : null

  // For auctions, we fetch a 6-item preview
  const auctionItemsPromise = isAuctionListing
    ? lotsClient.findItemsByLot({ lotId, limit: 6 }, reqOpts)
    : null

  // For offerings, we need to fetch the full item set
  const offeringItemsPromise = isAuctionListing
    ? null
    : fetchAllResults({
        apiClient: lotsClient,
        query: lotsClient.findItemsByLot,
        params: { lotId, status: [ItemDtoStatusEnum.Ready] },
        dataKey: 'items',
        axiosOptions: reqOpts,
      })

  const sellerAccountPromise = isAuctionListing
    ? null
    : accountsClient.getAccount({ id: sellerId }, reqOpts)

  const storefrontPromise = storefrontsClient.findOneStoreFront(
    { id: storefrontId },
    reqOpts
  )

  const { termsAcceptedPromise, termsErrorHandler, termsMetadataPromise } =
    buildTermsPromises({
      accessToken,
      termsRestrictions,
    })

  const [
    auction,
    auctionItemsResponse,
    contract,
    lotRes,
    offerings,
    offeringItems,
    searchData,
    sellerAccountResponse,
    storefront,
    [termsAcceptedResponse, termsMetadataResponse],
  ] = await Promise.all([
    auctionPromise,
    auctionItemsPromise,
    contractPromise,
    lotPromise,
    offeringsPromise,
    offeringItemsPromise,
    searchDataPromise,
    sellerAccountPromise,
    storefrontPromise,
    Promise.all([termsAcceptedPromise, termsMetadataPromise]).catch(
      termsErrorHandler
    ),
  ])

  const rawLot: LeanLot = lotRes.data
  let items: ItemDto[] = []
  let itemsTotal: number = 0

  if (auctionItemsResponse) {
    items = auctionItemsResponse.data.items
    itemsTotal = auctionItemsResponse.data.total
  } else if (offeringItems) {
    items = offeringItems
    itemsTotal = items.length
  }

  const offering = offerings?.at(0) ?? null

  const sellerAccount = sellerAccountResponse?.data ?? null

  const {
    deal = null,
    problemFreeOrderRate = null,
    typicalSellingPrice = null,
  } = searchData ?? {}

  const acceptedPolicies = termsAcceptedResponse?.data ?? null
  const termsMetadata = termsMetadataResponse?.data[0] ?? null

  /**
   * Step 3: fetches which depend on step 2 responses
   */
  const groupsPromise = groupsClient.findAllGroups(
    { id: rawLot.groups },
    reqOpts
  )

  // Fetch lot locations
  const augmentedLotsPromise = augmentLots([rawLot], accessToken)

  const offersPromise =
    offering && accessToken && !isAuctionListing
      ? offersClient.findOffers({ offeringId: [offering._id] }, reqOpts)
      : null

  // For LimitedAuction (buyers), getAuctionWithBid returns bid=null because
  // winningBidId is omitted. Fetch the user's actual bid separately so that
  // "Your current max" and "Enter max bid" display correctly after a page reload.
  const limitedAuctionBidPromise = (() => {
    if (!auction || !accessToken || !auction._id) return Promise.resolve(null)
    const asLimited = auction as { winning?: boolean; userBidAmount?: number }
    const isLimitedAuction = typeof asLimited.winning === 'boolean'
    const hasUserBid = asLimited.winning || (asLimited.userBidAmount ?? 0) > 0
    if (!isLimitedAuction || !hasUserBid || auction.bid != null)
      return Promise.resolve(null)
    return getBidsClient()
      .getAccountUniqueBids({ auctionId: auction._id, limit: 1 }, reqOpts)
      .then((res) => res.data.bids[0] ?? null)
      .catch(() => null)
  })()

  const [augmentedLots, groupsResponse, offersResponse, limitedAuctionBid] =
    await Promise.all([
      augmentedLotsPromise,
      groupsPromise,
      offersPromise,
      limitedAuctionBidPromise,
    ])

  const orchestratedLot = augmentedLots[0]
  if (!orchestratedLot) {
    throw new Error('Unable to augment lot with location information')
  }
  const { groups } = groupsResponse.data
  const lot: OrchestratedLotWithGroups = { ...orchestratedLot, groups }

  const offers = offersResponse?.data.offers ?? null

  const commonListingDetails: ListingDetailsCommon = {
    ...listing,
    acceptedPolicies,
    contract,
    deal,
    itemsTotal,
    lot,
    problemFreeOrderRate,
    seller: {
      storefront: storefront.data,
      account: getSellerInfo(isAuctionListing ? auction : sellerAccount),
    },
    termsMetadata,
    typicalSellingPrice,
  }

  if (isAuctionListing) {
    if (!auction) throw new Error('Failed to fetch auction for auction listing')

    const firstDibsWithWindowStart = withFirstDibsWindowStart(
      listing.firstDibs,
      auction
    )

    const auctionWithBid =
      limitedAuctionBid !== null
        ? { ...auction, bid: limitedAuctionBid }
        : auction

    return {
      ...commonListingDetails,
      ...(firstDibsWithWindowStart && { firstDibs: firstDibsWithWindowStart }),
      auction: patchBidStatusFromLimitedAuction(auctionWithBid),
      inventoryPreview: items,
      items: null,
      offering: null,
      offers: null,
    }
  }

  return {
    ...commonListingDetails,
    auction: null,
    inventoryPreview: null,
    items: augmentOfferingItems(items),
    offering,
    offers,
  }
}

export const buildTermsPromises = ({
  accessToken,
  termsRestrictions,
}: {
  accessToken: string | null
  termsRestrictions: string[] | undefined
}) => {
  const termsClient = getTermsClient()
  const reqOpts = buildAxiosRequestConfig({ accessToken })

  const term = termsRestrictions?.[0]

  const termsErrorHandler = (error: unknown) => {
    // If terms check fails, we'll let the page handle it
    console.error('Failed to fetch accepted policies or terms metadata:', error)
    return [null, null]
  }

  const nullPromises = {
    termsAcceptedPromise: null,
    termsErrorHandler,
    termsMetadataPromise: null,
  }

  if (!accessToken || !term) return nullPromises

  const decodedToken = decodeAccessToken(accessToken)
  const userData = augmentTokenPayload(decodedToken) ?? null

  const canViewWithoutAcceptingTerms = hasPermission({
    granule: PermissionGranule.FEBP_LISTING_DETAIL_VIEW_WITHOUT_ACCEPTING_TERMS,
    userData,
  })

  if (canViewWithoutAcceptingTerms) return nullPromises

  return {
    termsAcceptedPromise: termsClient.getAcceptedPolicies(
      { id: decodedToken.accountId },
      reqOpts
    ),
    termsErrorHandler,
    termsMetadataPromise: termsClient.getTermsMetadata({ term }, reqOpts),
  }
}

/**
 * Query orchestrated data for a listing by listingId.
 */
const listingDetailsQuery = makeOptionallyAuthenticatedQuery(
  'listingDetails',
  getListingDetails,
  {
    // Align with makeAuthenticatedQuery/makeUnauthenticatedQuery defaults so the
    // SSR-hydrated data isn't refetched on mount — an immediate on-mount refetch
    // re-sends `incrementViewCount` and double-counts views on every reload.
    staleTime: Infinity,
    // Intentionally exclude `incrementViewCount` from the query key
    makeKey: (name, [accessToken, id]) => [
      name,
      id,
      accessToken ? sanitizeAccessToken(accessToken) : null,
    ],
  }
)

export { getListingDetails }
export default listingDetailsQuery
